import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { AxiosError } from "axios";
import { api } from "@/lib/api";
import {
  Plus,
  Trash2,
  ShieldCheck,
  ShieldAlert,
  FileText,
  Eye,
  CheckCircle2,
  Circle,
  AlertCircle,
  Calendar,
} from "lucide-react";
import {
  DetailLayout,
  DetailLoading,
  DetailError,
  StatusBadge,
  formatDate,
} from "@/components/DetailLayout";
import { ConfirmModal } from "@/components/ConfirmModal";
import { UploadDocumentoModal } from "@/components/UploadDocumentoModal";
import { DocumentoPreviewModal } from "@/components/DocumentoPreviewModal";
import { DocumentosSeleccionables } from "@/components/DocumentosSeleccionables";

interface DocumentoVinculado {
  documentoId: string;
  versionVigenteId: string;
  numeroVersionVigente: number;
  tipoCodigo: string;
  tipoNombre: string;
  nombreArchivo: string;
  sizeBytes: number;
  antivirusEstado: string;
  fechaCarga: string;
}

interface ExpedienteDetalle {
  id: string;
  solicitudId: string;
  completo: boolean;
  fechaCompletado: string | null;
  observaciones: string | null;
  fechaCreacion: string;
  documentos: DocumentoVinculado[];
  /** Documentos cargados al contexto de la solicitud (no vinculados al expediente). */
  documentosSolicitud: DocumentoVinculado[];
  tiposRequeridosFaltantes: string[];
  autoTransicionDisparada: boolean;
}

interface TipoDocumentoResumen {
  id: string;
  codigo: string;
  nombre: string;
  descripcion: string | null;
  requiereCopiaCertificada: boolean;
  requeridoTelecomBasico: boolean;
}

export function ExpedienteTelecomPage() {
  const { solicitudId } = useParams<{ solicitudId: string }>();
  const qc = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [desvincular, setDesvincular] = useState<DocumentoVinculado | null>(null);
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);

  const q = useQuery<ExpedienteDetalle, AxiosError>({
    queryKey: ["expediente-telecom", solicitudId],
    queryFn: async () =>
      (await api.get<ExpedienteDetalle>(`/api/sector/telecom/expedientes/por-solicitud/${solicitudId}`)).data,
    enabled: !!solicitudId,
    retry: (failureCount, error) => {
      if (error.response?.status === 404) return false;
      return failureCount < 3;
    },
  });

  const qTipos = useQuery<TipoDocumentoResumen[]>({
    queryKey: ["documentos-tipos", "TELECOM"],
    queryFn: async () => (await api.get<TipoDocumentoResumen[]>(`/api/documentos/tipos?tag=TELECOM`)).data,
    enabled: !!q.data,
    staleTime: 5 * 60 * 1000,
  });

  if (q.isLoading) return <DetailLoading />;

  // 404: la solicitud no tiene expediente todavía
  if (q.error && (q.error as AxiosError).response?.status === 404) {
    return (
      <DetailLayout
        title="Expediente Telecom"
        subtitle={
          <>
            Para solicitud{" "}
            <Link to={`/sector/telecom/solicitudes/${solicitudId}`} className="hover:underline">
              {solicitudId}
            </Link>
          </>
        }
        backTo={`/sector/telecom/solicitudes/${solicitudId}`}
        backLabel="Volver a la solicitud"
      >
        <div className="rounded-lg border bg-surface p-6 shadow-card">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 text-estado-warn" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-fg">Sin expediente técnico</h3>
              <p className="mt-1 text-sm text-fg-muted">
                La solicitud todavía no tiene expediente técnico. Crear el expediente lo inicia vacío;
                después puedes subir y vincular documentos.
              </p>
              <button
                onClick={async () => {
                  try {
                    await api.post(`/api/sector/telecom/expedientes`, { solicitudId });
                    await qc.invalidateQueries({ queryKey: ["expediente-telecom", solicitudId] });
                  } catch {
                    /* el error ya se muestra al recargar la pantalla */
                  }
                }}
                className="mt-4 inline-flex cursor-pointer items-center gap-1 rounded bg-primary px-3 py-1.5 text-sm text-white transition hover:bg-primary-hover"
              >
                <Plus className="h-4 w-4" />
                Crear expediente
              </button>
            </div>
          </div>
        </div>
      </DetailLayout>
    );
  }

  if (q.error || !q.data) return <DetailError />;
  const e = q.data;
  const tiposRequeridos = (qTipos.data ?? []).filter((t) => t.requeridoTelecomBasico);
  const documentosPorTipo = new Map<string, DocumentoVinculado[]>();
  for (const d of e.documentos) {
    const arr = documentosPorTipo.get(d.tipoCodigo) ?? [];
    arr.push(d);
    documentosPorTipo.set(d.tipoCodigo, arr);
  }
  const cubiertos = tiposRequeridos.filter((t) => (documentosPorTipo.get(t.codigo)?.length ?? 0) > 0).length;
  const totalRequeridos = tiposRequeridos.length;
  const porcentaje = totalRequeridos === 0 ? 100 : Math.round((cubiertos / totalRequeridos) * 100);

  return (
    <DetailLayout
      title="Expediente Telecom"
      subtitle={
        <>
          Para solicitud{" "}
          <Link to={`/sector/telecom/solicitudes/${solicitudId}`} className="hover:underline">
            ver solicitud
          </Link>
        </>
      }
      backTo={`/sector/telecom/solicitudes/${solicitudId}`}
      backLabel="Volver a la solicitud"
      rawData={e}
    >
      <HeroEstado
        completo={e.completo}
        cubiertos={cubiertos}
        totalRequeridos={totalRequeridos}
        porcentaje={porcentaje}
        fechaCreacion={e.fechaCreacion}
        fechaCompletado={e.fechaCompletado}
        autoTransicionDisparada={e.autoTransicionDisparada}
        observaciones={e.observaciones}
        onSubir={() => setUploadOpen(true)}
      />

      {totalRequeridos > 0 && (
        <ChecklistRequisitos
          tipos={tiposRequeridos}
          documentosPorTipo={documentosPorTipo}
          loading={qTipos.isLoading}
        />
      )}

      <ListaDocumentos
        documentos={e.documentos}
        solicitudId={e.solicitudId}
        onSubir={() => setUploadOpen(true)}
        onVer={(id) => setPreviewDocId(id)}
        onDesvincular={setDesvincular}
      />

      {e.documentosSolicitud.length > 0 && (
        <DocumentosSolicitud
          documentos={e.documentosSolicitud}
          solicitudId={e.solicitudId}
          onVer={(id) => setPreviewDocId(id)}
        />
      )}

      <UploadDocumentoModal
        open={uploadOpen}
        title={`Subir y vincular documento al expediente`}
        contextoTipo="EXPEDIENTE_TELECOM"
        contextoId={e.id}
        tag="TELECOM"
        onClose={() => setUploadOpen(false)}
        onUploaded={async (documentoId) => {
          await api.post(`/api/sector/telecom/expedientes/${e.id}/documentos`, { documentoId });
          await qc.invalidateQueries({ queryKey: ["expediente-telecom", solicitudId] });
        }}
      />

      <ConfirmModal
        open={!!desvincular}
        title="Desvincular documento"
        body={
          desvincular && (
            <>
              Vas a desvincular <span className="font-medium">{desvincular.tipoNombre}</span>{" "}
              (<span className="font-mono text-xs">{desvincular.nombreArchivo}</span>). El
              documento permanece en el Documental, pero deja de estar asociado al expediente.
            </>
          )
        }
        motivoLabel="Motivo del desvínculo"
        motivoRequired
        confirmLabel="Desvincular"
        confirmTone="danger"
        onCancel={() => setDesvincular(null)}
        onConfirm={async (motivo) => {
          await api.delete(
            `/api/sector/telecom/expedientes/${e.id}/documentos/${desvincular!.documentoId}`,
            { data: { motivo } }
          );
          setDesvincular(null);
          await qc.invalidateQueries({ queryKey: ["expediente-telecom", solicitudId] });
        }}
      />

      {previewDocId && (
        <DocumentoPreviewModal
          documentoId={previewDocId}
          onClose={() => setPreviewDocId(null)}
        />
      )}
    </DetailLayout>
  );
}

function HeroEstado({
  completo,
  cubiertos,
  totalRequeridos,
  porcentaje,
  fechaCreacion,
  fechaCompletado,
  autoTransicionDisparada,
  observaciones,
  onSubir,
}: {
  completo: boolean;
  cubiertos: number;
  totalRequeridos: number;
  porcentaje: number;
  fechaCreacion: string;
  fechaCompletado: string | null;
  autoTransicionDisparada: boolean;
  observaciones: string | null;
  onSubir: () => void;
}) {
  const colorBarra = completo ? "bg-estado-ok" : porcentaje >= 60 ? "bg-estado-info" : "bg-estado-warn";
  const colorTexto = completo ? "text-estado-ok" : "text-estado-warn";
  const colorPunto = completo ? "bg-estado-ok" : "bg-estado-warn";

  return (
    <section className="rounded-lg border bg-surface p-5 shadow-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={`inline-block h-2.5 w-2.5 rounded-full ${colorPunto}`} aria-hidden />
            <span className={`text-sm font-semibold uppercase tracking-wide ${colorTexto}`}>
              {completo ? "Expediente completo" : "Expediente incompleto"}
            </span>
            {autoTransicionDisparada && (
              <StatusBadge tone="ok">Aprobación auto-disparada</StatusBadge>
            )}
          </div>
          <h2 className="mt-2 text-lg font-semibold text-fg">
            {totalRequeridos === 0
              ? "Sin requisitos básicos definidos"
              : `${cubiertos} de ${totalRequeridos} requisitos básicos cubiertos`}
          </h2>
          {totalRequeridos > 0 && (
            <div className="mt-3 w-full max-w-md">
              <div className="h-2 w-full overflow-hidden rounded-full bg-surface-2">
                <div
                  className={`h-full ${colorBarra} transition-all`}
                  style={{ width: `${porcentaje}%` }}
                />
              </div>
              <div className="mt-1 flex justify-between text-xs text-fg-subtle">
                <span>Progreso del checklist</span>
                <span className="font-medium text-fg-muted">{porcentaje}%</span>
              </div>
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-4 text-xs text-fg-muted">
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5 text-fg-faint" />
              Creado {formatDate(fechaCreacion, true)}
            </span>
            {fechaCompletado && (
              <span className="inline-flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5 text-estado-ok" />
                Completado {formatDate(fechaCompletado, true)}
              </span>
            )}
          </div>
          {observaciones && (
            <div className="mt-3 rounded border border-edge bg-surface-2 p-2 text-xs text-fg-muted">
              <span className="font-medium">Observaciones:</span> {observaciones}
            </div>
          )}
        </div>
        <button
          onClick={onSubir}
          className="inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-primary-hover"
        >
          <Plus className="h-4 w-4" />
          Subir y vincular documento
        </button>
      </div>
    </section>
  );
}

function ChecklistRequisitos({
  tipos,
  documentosPorTipo,
  loading,
}: {
  tipos: TipoDocumentoResumen[];
  documentosPorTipo: Map<string, DocumentoVinculado[]>;
  loading: boolean;
}) {
  if (loading) {
    return (
      <section className="rounded-lg border bg-surface shadow-card">
        <h2 className="border-b px-4 py-2 text-xs font-semibold uppercase tracking-wide text-fg-subtle">
          Checklist de requisitos básicos
        </h2>
        <div className="p-4 text-sm text-fg-subtle">Cargando requisitos…</div>
      </section>
    );
  }
  return (
    <section className="rounded-lg border bg-surface shadow-card">
      <h2 className="border-b px-4 py-2 text-xs font-semibold uppercase tracking-wide text-fg-subtle">
        Checklist de requisitos básicos
      </h2>
      <ul className="divide-y">
        {tipos.map((t) => {
          const docs = documentosPorTipo.get(t.codigo) ?? [];
          const cubierto = docs.length > 0;
          const todosLimpios = cubierto && docs.every((d) => d.antivirusEstado === "LIMPIO");
          return (
            <li key={t.codigo} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="flex min-w-0 items-center gap-3">
                {cubierto ? (
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-estado-ok" />
                ) : (
                  <Circle className="h-5 w-5 shrink-0 text-fg-faint" />
                )}
                <div className="min-w-0">
                  <div className={`text-sm font-medium ${cubierto ? "text-fg" : "text-fg-muted"}`}>
                    {t.nombre}
                  </div>
                  {t.descripcion && (
                    <div className="truncate text-xs text-fg-subtle" title={t.descripcion}>
                      {t.descripcion}
                    </div>
                  )}
                </div>
              </div>
              <div className="shrink-0 text-right text-xs">
                {cubierto ? (
                  <div className="flex items-center gap-2">
                    <span className="text-fg-muted">
                      {docs.length} {docs.length === 1 ? "documento" : "documentos"}
                    </span>
                    {todosLimpios ? (
                      <span className="inline-flex items-center gap-1 text-estado-ok">
                        <ShieldCheck className="h-3 w-3" /> Limpio
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-estado-warn">
                        <ShieldAlert className="h-3 w-3" /> Revisar
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="font-medium text-estado-warn">Falta</span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * Documentos vinculados al expediente. La selección múltiple, el ZIP y los
 * comentarios los pone {@link DocumentosSeleccionables}, el mismo componente que
 * usa Seguridad; aquí solo se añaden el antivirus y las acciones propias del
 * expediente: ver en el visor y desvincular.
 *
 * De consulta a propósito (`permitirEscritura={false}`): retirar un documento de
 * este expediente es desvincularlo, que además revierte la marca de completo.
 */
function ListaDocumentos({
  documentos,
  solicitudId,
  onSubir,
  onVer,
  onDesvincular,
}: {
  documentos: DocumentoVinculado[];
  solicitudId: string;
  onSubir: () => void;
  onVer: (id: string) => void;
  onDesvincular: (d: DocumentoVinculado) => void;
}) {
  if (documentos.length === 0) {
    return (
      <section className="rounded-lg border bg-surface shadow-card">
        <div className="flex items-center justify-between border-b px-4 py-2">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-fg-subtle">
            Documentos vinculados (0)
          </h2>
        </div>
        <div className="p-8 text-center">
          <FileText className="mx-auto h-10 w-10 text-fg-faint" />
          <p className="mt-2 text-sm text-fg-subtle">No hay documentos vinculados todavía.</p>
          <button
            onClick={onSubir}
            className="mt-3 inline-flex cursor-pointer items-center gap-1 rounded border border-primary-strong px-3 py-1.5 text-sm text-primary-strong transition hover:bg-primary-tint"
          >
            <Plus className="h-4 w-4" />
            Subir y vincular el primero
          </button>
        </div>
      </section>
    );
  }
  return (
    <section className="rounded-lg border bg-surface shadow-card">
      <div className="flex items-center justify-between border-b px-4 py-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-fg-subtle">
          Documentos vinculados ({documentos.length})
        </h2>
        <button
          onClick={onSubir}
          className="inline-flex cursor-pointer items-center gap-1 rounded border border-primary-strong px-2.5 py-1 text-xs text-primary-strong transition hover:bg-primary-tint"
        >
          <Plus className="h-3.5 w-3.5" />
          Subir más
        </button>
      </div>
      <div className="px-4 py-2">
        <DocumentosSeleccionables
          docs={documentos}
          tramiteId={solicitudId}
          permitirEscritura={false}
          motivoDescarga="Descarga desde el expediente Telecom"
          renderMeta={(d) => <MetaDocumento antivirusEstado={(d as DocumentoVinculado).antivirusEstado} fechaCarga={d.fechaCarga} />}
          renderAccionExtra={(d) => (
            <div className="flex shrink-0 items-center gap-1">
              <BotonVer onClick={() => onVer(d.documentoId)} />
              <button
                onClick={() => onDesvincular(d as DocumentoVinculado)}
                className="inline-flex cursor-pointer items-center gap-1 rounded border border-estado-err/25 px-2 py-1 text-xs text-estado-err transition hover:bg-estado-err-bg"
                title="Desvincular del expediente"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          )}
        />
      </div>
    </section>
  );
}

/** Fecha de carga y resultado del antivirus, igual en las dos listas. */
function MetaDocumento({ antivirusEstado, fechaCarga }: { antivirusEstado: string; fechaCarga: string }) {
  return (
    <>
      <span>· Cargado {formatDate(fechaCarga, true)}</span>
      {antivirusEstado === "LIMPIO" ? (
        <span className="inline-flex items-center gap-1 text-estado-ok">
          <ShieldCheck className="h-3 w-3" /> Antivirus limpio
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-estado-warn">
          <ShieldAlert className="h-3 w-3" /> Antivirus: {antivirusEstado}
        </span>
      )}
    </>
  );
}

/** Abre el visor, que es donde Telecom consulta el documento antes de bajarlo. */
function BotonVer({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex cursor-pointer items-center gap-1 rounded border border-edge px-2 py-1 text-xs text-fg-muted transition hover:bg-surface-2"
      title="Ver documento (vista previa y descarga)"
    >
      <Eye className="h-3 w-3" /> Ver
    </button>
  );
}

function DocumentosSolicitud({
  documentos,
  solicitudId,
  onVer,
}: {
  documentos: DocumentoVinculado[];
  solicitudId: string;
  onVer: (id: string) => void;
}) {
  return (
    <section className="rounded-lg border bg-surface shadow-card">
      <div className="flex items-center justify-between border-b px-4 py-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-fg-subtle">
          Documentos de la solicitud ({documentos.length})
        </h2>
      </div>
      <div className="border-b bg-estado-info-bg px-4 py-2 text-xs text-estado-info">
        Documentos cargados durante el trámite. Se muestran aquí para consulta; no requieren
        vincularse manualmente al expediente.
      </div>
      <div className="px-4 py-2">
        <DocumentosSeleccionables
          docs={documentos}
          tramiteId={solicitudId}
          permitirEscritura={false}
          motivoDescarga="Descarga desde el expediente Telecom"
          renderMeta={(d) => <MetaDocumento antivirusEstado={(d as DocumentoVinculado).antivirusEstado} fechaCarga={d.fechaCarga} />}
          renderAccionExtra={(d) => <BotonVer onClick={() => onVer(d.documentoId)} />}
        />
      </div>
    </section>
  );
}
